import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { TrendingUp } from 'lucide-react'
import { apiClient } from '@/lib/api.ts'
import { StatTile, PeriodSelect, PERIOD_OPTIONS } from './StatTile.tsx'

interface TopArtikelItem {
  itemCode: string
  itemDescription: string | null
  invoiceCount: number
  totalQty: number
  totalRevenue: number
  totalGrossProfit: number
  avgMarginPct: number
  ekInvoiceCount: number
  ekCatalogCount: number
  ekNoneCount: number
}

function fmt(v: number) {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v)
}

interface Props {
  tenantId?: string | null
}

export function TopArtikelRohertragTile({ tenantId }: Props) {
  const [days, setDays] = useState(90)

  const { data, isLoading, error } = useQuery<{ items: TopArtikelItem[] }>({
    queryKey: ['stats.top-articles', days, tenantId],
    queryFn: () => {
      const params = new URLSearchParams({ days: String(days), limit: '10' })
      if (tenantId) params.set('tenantId', tenantId)
      return apiClient.get(`/stats/top-articles?${params}`).then((r) => r.data)
    },
    staleTime: 5 * 60 * 1000,
  })

  const items = data?.items ?? []
  const maxProfit = items.length > 0 ? Math.max(...items.map((i) => i.totalGrossProfit)) : 1

  return (
    <StatTile
      title="Top-Artikel nach Rohertrag"
      subtitle="Tatsächlich erwirtschafteter Deckungsbeitrag aus Rechnungen"
      loading={isLoading}
      error={error as Error | null}
      empty={!isLoading && !error && items.length === 0}
      emptyMessage="Noch keine Rechnungsdaten synchronisiert. Bitte den Sync unter Einstellungen → Scheduled Jobs starten."
      headerRight={
        <PeriodSelect value={days} options={PERIOD_OPTIONS} onChange={setDays} />
      }
    >
      <div className="space-y-2">
        {items.map((item, i) => {
          const pct = maxProfit > 0 ? (item.totalGrossProfit / maxProfit) * 100 : 0
          const hasEstimatedEk = item.ekCatalogCount > 0 || item.ekNoneCount > 0
          return (
            <div key={item.itemCode} className="group">
              <div className="flex items-center justify-between gap-2 mb-1">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-caption text-gray-400 w-5 shrink-0">#{i + 1}</span>
                  <div className="min-w-0">
                    <span className="text-small font-medium text-gray-900 truncate block">
                      {item.itemDescription ?? item.itemCode}
                    </span>
                    <span className="text-caption text-gray-400">{item.itemCode}</span>
                  </div>
                </div>
                <div className="shrink-0 text-right">
                  <div className="flex items-center gap-1">
                    <span className="text-small font-semibold text-gray-900">
                      {fmt(item.totalGrossProfit)}
                    </span>
                    {hasEstimatedEk && (
                      <span className="text-caption text-amber-500" title="Teilweise geschätzter EK">~</span>
                    )}
                  </div>
                  <span className={`text-caption ${item.avgMarginPct >= 10 ? 'text-green-600' : item.avgMarginPct > 0 ? 'text-amber-600' : 'text-red-600'}`}>
                    Ø {item.avgMarginPct.toFixed(1)} %
                  </span>
                </div>
              </div>
              <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className={`h-1.5 rounded-full transition-all ${item.totalGrossProfit >= 0 ? 'bg-green-500' : 'bg-red-400'}`}
                  style={{ width: `${Math.max(pct, 2)}%` }}
                />
              </div>
            </div>
          )
        })}
      </div>
      <div className="flex items-center gap-1 mt-3 pt-3 border-t border-gray-100">
        <TrendingUp className="w-3 h-3 text-gray-400" />
        <span className="text-caption text-gray-400">Rohertrag = VK − EK (Ist oder Katalogpreis)</span>
      </div>
    </StatTile>
  )
}
